import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Flower, Dog, CloudSun, Leaf } from "@phosphor-icons/react";
import { http } from "@/lib/api";

// Global Environmental Catalogue — four macro categories with live counts.
// Counts derive from /api/observations grouped by category (best-effort);
// seed values shown while loading.

const TILES = [
  { key: "flora",     label: "Flora",     Icon: Flower,   seed: 5,
    match: ["plants", "agriculture", "fungi"] },
  { key: "fauna",     label: "Fauna",     Icon: Dog,      seed: 7,
    match: ["wildlife", "birds", "insects", "aquatic"] },
  { key: "weather",   label: "Weather",   Icon: CloudSun, seed: 12,
    match: ["weather"] },
  { key: "ecosystems",label: "Ecosystems",Icon: Leaf,     seed: 3,
    match: ["landscapes", "water", "geology"] },
];

export default function GlobalCatalogue() {
  const [counts, setCounts] = useState(
    TILES.reduce((acc, t) => ({ ...acc, [t.key]: t.seed }), {})
  );

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const { data } = await http.get("/observations?limit=500");
        if (!ok) return;
        const next = TILES.reduce((acc, t) => ({ ...acc, [t.key]: 0 }), {});
        for (const o of data.observations || []) {
          for (const t of TILES) {
            if (t.match.includes((o.category || "").toLowerCase())) {
              next[t.key] += 1;
            }
          }
        }
        // Merge seeds so tiles never look empty on a fresh install
        for (const t of TILES) {
          if (next[t.key] === 0) next[t.key] = t.seed;
        }
        setCounts(next);
      } catch { /* keep seeds */ }
    })();
    return () => { ok = false; };
  }, []);

  return (
    <section className="bg-[#0A1620] text-[color:var(--np-cream,#F5EBDC)] pb-16 md:pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="font-mono-tech text-[11px] md:text-xs tracking-[0.35em] uppercase text-[#E5D8C4]/80">
            Global Environmental Catalogue
          </h2>
          <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5D8C4]/50">
            /03 · Living index
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {TILES.map((t, i) => (
            <motion.div
              key={t.key}
              data-testid={`catalogue-tile-${t.key}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="relative border border-[#F5EBDC]/12 p-6 md:p-8 hover:border-[#E4A15C]/70 transition-colors group"
            >
              <t.Icon size={40} weight="thin" className="text-[#E4A15C] mb-4 group-hover:scale-110 transition-transform" />
              <div className="font-serif-display text-2xl text-[#F5EBDC]">{t.label}</div>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5D8C4]/60 mt-1">
                ({counts[t.key]} {counts[t.key] === 1 ? "count" : "counts"})
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
